/**
 * ELF-CIRCLE — The Trifecta (design artboard `#3a`).
 *
 * Three pillar panels share one row; the selected one expands to 3.4x its
 * siblings and reveals its detail. Expansion is CSS `flex-grow` driven by
 * `aria-pressed`, so this component only tracks which pillar is open.
 *
 * Static page — pillar art is bundled, not an editable CMS slot.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import entertainmentArt from '../../../../../assets/elf/trifecta-entertainment.jpg';
import fashionArt from '../../../../../assets/elf/trifecta-fashion.jpg';
import lifestyleArt from '../../../../../assets/elf/trifecta-lifestyle.jpg';

interface Pillar {
  num: string;
  name: string;
  art: string;
  blurb: string;
  builtFor: string[];
}

const PILLARS: Pillar[] = [
  {
    num: '01',
    name: 'Entertainment',
    art: entertainmentArt,
    blurb: 'Talent, film, music, festivals, live events',
    builtFor: ['Artists', 'Labels', 'Venues', 'Promoters', 'Music', 'Festivals'],
  },
  {
    num: '02',
    name: 'Lifestyle',
    art: lifestyleArt,
    blurb: 'Culture-forward brands, wellness, hospitality, media',
    builtFor: ['Founders', 'Creators', 'Community brands', 'Wellness', 'Hospitality', 'Media'],
  },
  {
    num: '03',
    name: 'Fashion',
    art: fashionArt,
    blurb: 'Designers, retail, product placement, runway-to-retail',
    builtFor: [
      'Designers',
      'Models',
      'Boutique retail',
      'Enterprise retail',
      'Runway-to-retail',
      'Product placement',
    ],
  },
];

export const TheTrifecta: React.FC = () => {
  const [open, setOpen] = useState(0);
  const consult = useConsult();

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <ElfPageNav current="/the-trifecta" />
          <div style={{ padding: '18px var(--pgut) 0' }}>
            <ElfCrumb
              trail={[
                { label: 'Home', to: '/' },
                { label: 'Who We Are', to: '/who-we-are' },
                'The Trifecta',
              ]}
            />
          </div>

          <div
            className="elf-page__split"
            style={{
              padding: '40px var(--pgut) 26px',
              display: 'grid',
              gridTemplateColumns: '1fr 400px',
              gap: 56,
              alignItems: 'end',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <span className="eyebrow">
                <i />
                Positioning framework
              </span>
              <h1 className="elf-page__h1">
                Three pillars.
                <br />
                <span style={{ color: 'var(--acc-panel)' }}>One filter.</span>
              </h1>
            </div>
            <p className="wbody" style={{ fontSize: 16 }}>
              Everything we do sits inside three pillars — the wireframe already names them, and
              they should anchor every page, not just the homepage.
            </p>
          </div>

          <div className="elf-page__tris" style={{ padding: '14px var(--pgut) 52px', display: 'flex', gap: 14 }}>
            {PILLARS.map((p, i) => (
              <button
                key={p.name}
                className="tri"
                type="button"
                aria-pressed={i === open}
                style={{ height: 520 }}
                onClick={() => setOpen(i)}
                onPointerEnter={(e) => {
                  // Mouse only: on touch this fires as a scroll begins; a tap arrives as the click.
                  if (e.pointerType === 'mouse') setOpen(i);
                }}
                onFocus={() => setOpen(i)}
              >
                <img className="tri__art" src={p.art} alt="" />
                <span className="tri__shade" />
                <span className="trivert">{p.name}</span>
                <span
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: '30px 32px',
                    boxSizing: 'border-box',
                  }}
                >
                  <span className="trinum">{p.num}</span>
                  <span
                    className="tribody"
                    style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}
                  >
                    <span className="pillarname" style={{ fontSize: 38 }}>
                      {p.name}
                    </span>
                    <span className="wbar" style={{ width: 70 }} />
                    <span className="wbody" style={{ fontSize: 15.5, maxWidth: '44ch' }}>
                      {p.blurb}
                    </span>
                    <span className="colhead">Built for</span>
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                      {p.builtFor.map((b) => (
                        <span className="bfchip" key={b}>
                          {b}
                        </span>
                      ))}
                    </span>
                  </span>
                </span>
              </button>
            ))}
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/services')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Next</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Two disciplines, one firm
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Marketing and web development, under one roof.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <button
              type="button"
              className="nextlink"
              onClick={() => consult?.open({ source: 'trifecta.consult', title: 'Book a consultation' })}
            ><div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Start here</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Book a consultation
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Tell us which pillar you live in.
                </span>
              </div>
              <span className="arw">→</span>
            </button>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default TheTrifecta;
